import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import FormRenderer, { ComponentMapper, FormTemplate, componentTypes } from "../../../common/FormRenderer";
import { getValue, toBackendDateFormat } from "../../../../utils/common";
import { useDeleteComplianceDocument, useGetComplianceActivityDocuments, useGetComplianceById, useRecallCompliance, useSubmitComplianceActivity, useUpdateComplianceSchedule, useUploadDocument } from "../../../../backend/compliance";
import styles from "./Styles.module.css";
import Icon from "../../../common/Icon";
import { ACTIONS } from "../../../common/Constants";
import { validatorTypes } from "@data-driven-forms/react-form-renderer";
import { COMPLIANCE_ACTIVITY_INDICATION, ComplianceActivityStatus, ComplianceStatusIconMapping, ComplianceStatusMapping } from "../../../../constants/Compliance.constants";
import { EVIDENCE_TYPE } from "../../../../utils/constants";
import ConfirmModal from "../../../common/ConfirmModal";
import PageLoader from "../../../shared/PageLoader";
import { download, downloadWithPresignedUrl } from "../../../../utils/common";
import { hasUserAccess, isAdmin } from "../../../../backend/auth";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { AxiosError } from "axios";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { FILE_TYPE_GROUPS, FILE_SIZE_LIMITS, generateFileRegex, generateAcceptAttribute, VALIDATION_MESSAGES } from "../../../../utils/fileValidation";

function isFormEditable(status: ComplianceActivityStatus) {
    if (hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)) {
        return [ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT, ComplianceActivityStatus.REJECTED, ComplianceActivityStatus.RECALLED].includes(status);
    } else if (hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD)) {
        return status === ComplianceActivityStatus.PENDING;
    }
    return false;
}

function hasEditableAccess() {
    return hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_SUBMIT)
        || hasUserAccess(USER_PRIVILEGES.MANAGER_ACTIVITIES_REVIEW);
}

export default function ComplianceActivityDetails(this: any, { data, onCancel, onSubmit }: any) {
    const [t] = useState(new Date().getTime());
    const [form, setForm] = useState<any>({ valid: true });
    const [formData, setFormData] = useState<any>();
    const [action, setAction] = useState(ACTIONS.NONE);
    const [submitComment, setSubmitComment] = useState('');
    const [editableForm, setEditable] = useState(isFormEditable(data.status));
    const [activityDetails, setActivity] = useState<any>(null);
    const [document, setDocument] = useState<any>();
    const [ruleCompliance, setRuleCompliance] = useState<any>(null);
    const [auditedDate, setAuditedDate] = useState<any>(null);
    const [auditDateInitial, setAuditDateInitial] = useState<Date | undefined>(undefined);
    const [isAuditDateChanged, setIsAuditDateChanged] = useState(false);
    const [recallRemarks, setRecallRemarks] = useState('');
    const [recallDueDate, setRecallDueDate] = useState('');
    // const { activity, isFetching, invalidate } = useGetComplianceById(data.id, { t });
    const activity = data;
    const { documents, refetch } = useGetComplianceActivityDocuments({ filters: {compliance_id_eq: data.id}});
    const [initialValue, setInitialValue] = useState<any>({ hideButtons: !(editableForm && hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_DOCUMENT_UPLOAD)), buttonWrapStyles: styles.btnGroup, submitBtnText: 'Upload' })
    const { uploadDocument } = useUploadDocument((response: any) => {
        if (response instanceof AxiosError){
            const data = (response.response || {}).data;
            const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
        } else {
            refetch();
            setInitialValue({ ...initialValue, file: null });
        }
    },
    (error: any) => {
        if (error instanceof AxiosError){
            const data = (error.response || {}).data;
            error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
        } else {
            toast.error(ERROR_MESSAGES.DEFAULT);
        }
    })
    const { updateComplianceSchedule: submitComplianceActivity } = useUpdateComplianceSchedule((response: any) => {
        if (response instanceof AxiosError){
            const data = (response.response || {}).data;
            const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
        } else {
            toast.success('Compliance activity submitted successfully.');
            // invalidate();
            onSubmit();
        }
    });
    const { updateComplianceSchedule } = useUpdateComplianceSchedule((response: any) => {
        if (response instanceof AxiosError){
            const data = (response.response || {}).data;
            const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
        } else {
            toast.success('Compliance activity reviewed successfully.');
            // invalidate();
            onSubmit();
        }
    });
    const { updateComplianceSchedule: updateAuditDate } = useUpdateComplianceSchedule((response: any) => {
        if (response instanceof AxiosError){
            const data = (response.response || {}).data;
            const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
        } else {
            toast.success('Actual completion date updated successfully.');
            onSubmit();
        }
    });
    const { recallCompliance, recalling } = useRecallCompliance((response: any) => {
        if (response instanceof AxiosError){
            const data = (response.response || {}).data;
            const error = (data || {}).error || ERROR_MESSAGES.DEFAULT;
            toast.error(error);
        } else {
            toast.success('Compliance activity recalled successfully.');
            onSubmit();
        }
    },
    (error: any) => {
        const data = (error && error.response && error.response.data) || {};
        toast.error(data.error || data.message || ERROR_MESSAGES.DEFAULT);
    });
    const { deleteDocument } = useDeleteComplianceDocument(() => {
        refetch();
        setAction(ACTIONS.NONE);
        setDocument(undefined);
    })

    const canEditAuditDate =
        (hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_SUBMIT) &&
            [ComplianceActivityStatus.DUE, ComplianceActivityStatus.NON_COMPLIANT, ComplianceActivityStatus.REJECTED, ComplianceActivityStatus.RECALLED].includes(data.status)) ||
        (hasUserAccess(USER_PRIVILEGES.MANAGER_ACTIVITIES_REVIEW) &&
            (data.status === ComplianceActivityStatus.PENDING || data.status === ComplianceActivityStatus.APPROVED));

    // Completion Date / Owner Comments are only meaningful to a Compliance Manager once the
    // owner has submitted the activity for review (Pending/Approved/Rejected) - hide them for
    // a manager viewing a Due/Non-Compliant/Recalled activity that hasn't reached review yet.
    const isManagerViewer = hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD);
    const isManagerReviewStatus = [ComplianceActivityStatus.PENDING, ComplianceActivityStatus.APPROVED, ComplianceActivityStatus.REJECTED].includes(data.status);
    const showManagerReviewFields = !isManagerViewer || isManagerReviewStatus;

    const schema = {
        fields: [
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'description',
                label: 'Description',
                content: getValue(activityDetails, 'activity.description'),
                className: 'grid-col-100',
                condition: {
                    when: 'description',
                    is: Boolean(getValue(activityDetails, 'activity.description')),
                    then: { visible: true }
                },
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'act',
                label: 'Act Name',
                content: getValue(activityDetails, 'act.name'),
                className: 'grid-col-66'
            },
            {
                component: componentTypes.WIZARD,
                name: 'emptySpace2',
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'lawCategory',
                label: 'Law Category',
                content: getValue(activityDetails, 'act.law.name') || '-NA-'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'activityType',
                label: 'Activity Type',
                content: getValue(activityDetails, 'activity.activity_type')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'establishment_type',
                label: 'Establishment Type',
                content: getValue(activityDetails, 'act.establishment_type')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'status',
                label: 'Status',
                content: ComplianceStatusMapping[getValue(activityDetails, 'status')]
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'dueDate',
                label: 'Due Date',
                content: dayjs(getValue(activityDetails, 'due_date')).format('DD-MM-YYYY')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'periodicity',
                label: 'Periodicity',
                content: getValue(activityDetails, 'activity.periodicity')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'company',
                label: 'Company',
                content: getValue(activityDetails, 'company.name'),
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'associate_company',
                label: 'Associate Company',
                content: getValue(activityDetails, 'associate_company.name'),
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'location',
                label: 'Location',
                content: getValue(activityDetails, 'location.name'),
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'vertical',
                label: 'Vertical',
                content: getValue(activityDetails, 'vertical.name'),
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'department',
                label: 'Department',
                content: getValue(activityDetails, 'department.name'),
            },
            {
                component: componentTypes.WIZARD,
                name: 'emptySpace3',
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'owner',
                label: 'Owner',
                content: getValue(activityDetails, 'compliance_owner.name'),
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'manager',
                label: 'Manager',
                content: getValue(activityDetails, 'compliance_manager.name'),
            },
            {
                component: componentTypes.WIZARD,
                name: 'emptySpace4',
            },
            {
                component: componentTypes.TAB_ITEM,
                name: 'subHeader1',
                content: 'Rule Compliance',
                className: 'grid-col-100 text-lg fw-bold pb-0'
            },
            {
                component: componentTypes.TAB_ITEM,
                name: 'subHeader11',
                content: getValue(ruleCompliance, 'compliance_description'),
                className: 'grid-col-100 pb-0',
                condition: {
                    when: 'subHeader11',
                    is: () => {
                        return Boolean(getValue(ruleCompliance, 'compliance_description'))
                    },
                    then: { visible: true }
                }
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'rule',
                label: 'Rule Name',
                content: getValue(activityDetails, 'rule.name')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'ruleNo',
                label: 'Rule No.',
                content: getValue(activityDetails, 'rule.rule_no') || '-NA-'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'ruleSectionNo',
                label: 'Section No.',
                content: getValue(activityDetails, 'rule.section_no') || '-NA-'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'ruleDescription',
                label: 'Rule Description',
                content: getValue(activityDetails, 'rule.description'),
                className: 'grid-col-100'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'risk',
                label: 'Risk',
                content: getValue(ruleCompliance, 'risk')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'auditType',
                label: 'Audit Type',
                content: getValue(ruleCompliance, 'audit_type')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'statutoryAuthority',
                label: 'Statutory Authority',
                content: getValue(ruleCompliance, 'statutory_authority'),
                condition: {
                    when: 'auditType',
                    is: () => {
                        const auditType = getValue(ruleCompliance, 'audit_type');
                        return auditType && auditType === 'Statutory';
                    },
                    then: { visible: true }
                }
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'penalty',
                label: 'Penalty',
                content: getValue(ruleCompliance, 'penalty'),
                condition: {
                    when: 'penalty',
                    is: () => {
                        return Boolean(activity) &&Boolean(ruleCompliance);
                    },
                    then: { visible: true }
                },
                className: 'grid-col-66'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'maximumPenaltyAmount',
                label: 'Max Penalty Amount',
                content: getValue(ruleCompliance, 'maximum_penalty_amount')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'proofOfCompliance',
                label: 'Proof Of Compliance',
                content: getValue(ruleCompliance, 'proof_of_compliance') || '-NA-',
                className: 'grid-col-100'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'impriosonment',
                label: 'Imprisonment',
                content: getValue(ruleCompliance, 'impriosonment', 'BOOLEAN')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'continuingPenalty',
                label: 'Continuing Penalty',
                content: getValue(ruleCompliance, 'continuing_penalty', 'BOOLEAN')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'cancellationSuspensionOfLicense',
                label: 'Cancellation/Suspension of License',
                content: getValue(ruleCompliance, 'cancellation_suspension_of_license', 'BOOLEAN')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'formName',
                label: 'Form Name',
                content: getValue(activityDetails, 'act_state_mapping.form_name') || '-NA-'
            },
            ...(showManagerReviewFields ? [{
                component: canEditAuditDate ? componentTypes.DATE_PICKER : componentTypes.PLAIN_TEXT,
                name: 'auditDate',
                label: 'Actual Completion Date',
                content: (() => {
                    const status = getValue(activityDetails, 'status');

                     // Hide date if rejected or recalled
                    if (status === ComplianceActivityStatus.REJECTED || status === ComplianceActivityStatus.RECALLED) {
                        return '-';
                    }
                    const val = getValue(activityDetails, 'audited_date');
                    const d = val && dayjs(val);
                    return d && d.isValid() && d.year() > 100 ? d.format('DD-MM-YYYY') : '-';
                })(),
                initialValue: auditDateInitial,
                disabled: !canEditAuditDate,
                clearable: false,
                maxDate: new Date(),
                onChange: (val: any) => {
                    setAuditedDate(val);
                    const selectedDay = val ? dayjs(val instanceof Date ? val : val.toDate()).format('YYYY-MM-DD') : null;
                    const originalDay = auditDateInitial ? dayjs(auditDateInitial).format('YYYY-MM-DD') : null;
                    setIsAuditDateChanged(selectedDay !== originalDay);
                },
            }] : []),
            // {
            //     component: componentTypes.PLAIN_TEXT,
            //     name: 'ownerRemarksReadOnly',
            //     label: 'Owner Remarks',
            //     content: getValue(activityDetails, 'forms_status_remarks'),
            //     className: 'grid-col-100',
            //     condition: {
            //         when: 'ownerRemarksReadOnly',
            //         is: () => {
            //             return hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) &&
            //                 Boolean(getValue(activityDetails, 'forms_status_remarks'))
            //         },
            //         then: { visible: true }
            //     }
            // },
            ...((hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)) &&
                (data.status === ComplianceActivityStatus.APPROVED || data.status === ComplianceActivityStatus.REJECTED) &&
                Boolean((getValue(activityDetails, 'audit_remarks') || '').trim()) ? [{
                component: componentTypes.PLAIN_TEXT,
                name: 'remarksFromManager',
                label: 'Remarks by Compliance Manager',
                content: getValue(activityDetails, 'audit_remarks'),
                className: 'grid-col-100',
            }] : []),
            ...((isManagerViewer ? isManagerReviewStatus : hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)) && Boolean((getValue(activityDetails, 'forms_status_remarks') || '').trim()) ? [{
                component: componentTypes.PLAIN_TEXT,
                name: 'commentsByOwner',
                label: 'Comments added by Compliance Owner',
                content: getValue(activityDetails, 'forms_status_remarks'),
                className: 'grid-col-100',
            }] : []),
            ...(data.status === ComplianceActivityStatus.RECALLED &&
                Boolean((getValue(activityDetails, 'metadata.recall_remarks') || '').trim()) ? [{
                component: componentTypes.PLAIN_TEXT,
                name: 'recallRemarksReadOnly',
                label: 'Recall Remarks',
                content: (() => {
                    const recalledAt = getValue(activityDetails, 'metadata.recalled_at');
                    const d = recalledAt && dayjs(recalledAt);
                    const remarks = getValue(activityDetails, 'metadata.recall_remarks');
                    return d && d.isValid() ? `${remarks} (Recalled on ${d.format('DD-MM-YYYY')})` : remarks;
                })(),
                className: 'grid-col-100',
            }] : []),
            // {
            //     component: componentTypes.PLAIN_TEXT,
            //     name: 'formsStatusRemarksReadOnly',
            //     label: 'Remarks',
            //     content: getValue(activityDetails, 'forms_status_remarks'),
            //     className: 'grid-col-100',
            //     condition: {
            //         when: 'formsStatusRemarksReadOnly',
            //         is: () => {
            //             const status = getValue(data, 'status');
            //             return hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) &&
            //                 (status === ComplianceActivityStatus.APPROVED || status === ComplianceActivityStatus.REJECTED)
            //         },
            //         then: { visible: true }
            //     }
            // },
        ]
    }

    const schema2 = {
        fields: [
            {
                component: componentTypes.DOCUMENTS_UPLOAD_NEW,
                name: 'documentsUpload',
                upload: editableForm && hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_DOCUMENT_UPLOAD),
                documents,
                delete: editableForm && hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_DOCUMENT_UPLOAD),
                accept: generateAcceptAttribute(FILE_TYPE_GROUPS.ALL_DOCUMENTS),
                validate: editableForm && hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_DOCUMENT_UPLOAD) ? [
                    { type: 'file-type', regex: generateFileRegex(FILE_TYPE_GROUPS.ALL_DOCUMENTS) },
                    { type: 'file-size', maxSize: FILE_SIZE_LIMITS.LARGE }
                ] : [],
                className: 'w-33',
                helperText: VALIDATION_MESSAGES.ALLOWED_FILE_ALL_DOCS,
                downloadDocument: downloadDocument.bind(this),
                deleteDocument: handleDeleteDocument.bind(this),
                // viewDocument: viewDocument.bind(this),
                onUpload: handleFileUpload.bind(this),
                multiple: false,
            },
            {
                component: componentTypes.TAB_ITEM,
                name: 'reviewSubHeader',
                content: 'Review',
                className: 'grid-col-100 text-lg fw-bold pb-0',
                condition: {
                    when: 'reviewSubHeader',
                    is: () => {
                        const status = getValue(data, 'status');
                        return hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) &&
                            status !== ComplianceActivityStatus.DUE && status !== ComplianceActivityStatus.NON_COMPLIANT
                    },
                    then: { visible: true }
                }
            },
            {
                component: editableForm ? componentTypes.SELECT : componentTypes.PLAIN_TEXT,
                name: 'status',
                label: 'Compliance Status',
                options: [
                    { id: ComplianceActivityStatus.APPROVED, name: 'Approve' },
                    { id: ComplianceActivityStatus.REJECTED, name: 'Reject' }
                ],
                className: 'w-33',
                content: getValue(activityDetails, 'status'),
                validate: [
                    { type: validatorTypes.REQUIRED },
                ],
                condition: {
                    when: 'status',
                    is: () => {
                        const status = getValue(data, 'status');
                        return hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) &&
                            status !== ComplianceActivityStatus.DUE && status !== ComplianceActivityStatus.NON_COMPLIANT
                    },
                    then: { visible: true }
                }
            },
            {
                component: editableForm ? componentTypes.TEXTAREA : componentTypes.PLAIN_TEXT,
                name: 'auditRemarks',
                label: 'Remarks',
                className: 'w-33',
                validate: [
                    { type: validatorTypes.REQUIRED }
                ],
                content: getValue(activityDetails, 'audit_remarks'),
                condition: {
                    when: 'auditRemarks',
                    is: () => {
                        const status = getValue(data, 'status');
                        return hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) &&
                            status !== ComplianceActivityStatus.DUE && status !== ComplianceActivityStatus.NON_COMPLIANT
                    },
                    then: { visible: true }
                }
            }
        ]
    }

    const schema3 = {
        fields: [
            {
                component: componentTypes.TAB_ITEM,
                name: 'documentSubHeader',
                content: 'Notice Details',
                className: 'grid-col-100 text-lg fw-bold pb-0',
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'noticeDescription',
                label: 'Notice Description',
                content: getValue(activityDetails, 'notice_description'),
                className: 'grid-col-100'
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'issuedAuthority',
                label: 'Issued Authority',
                content: getValue(activityDetails, 'notice_description')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'issuedDepartment',
                label: 'Issued Department',
                content: getValue(activityDetails, 'issued_department')
            },
            {
                component: componentTypes.PLAIN_TEXT,
                name: 'noticeDate',
                label: 'noticeDate',
                content: dayjs(getValue(activityDetails, 'notice_date')).format('DD-MM-YYYY')
            }
        ]
    }

    function TitleTmpl({ name, status }: any) {
        return (
            <div className="d-flex flex-row gap-2 mb-2">
                <Icon name={ComplianceStatusIconMapping[status]} className="text-xl" text={ComplianceStatusMapping[status]}
                    style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '', fill: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }} />
                <span className={`text-xl fw-bold ellipse`}
                    style={{ color: COMPLIANCE_ACTIVITY_INDICATION[status] || '' }}>{name}</span>
            </div>
        )
    }

    function debugForm(_form: any) {
        setForm(_form);
        setFormData(_form.values);
    }

    function downloadDocument({ file_name, file_path }: any) {
        downloadWithPresignedUrl(file_name, file_path);
    }

    function performSubmit() {
        if (hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)) {
            setAction(ACTIONS.CONFIRM);
        } else if (hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD)) {
            const { status, auditRemarks } = formData;
            updateComplianceSchedule({
                // ...activityDetails,
                compliance: {
                    id: activityDetails.id,
                    status: status.value,
                    audit_remarks: auditRemarks,
                    audited_date: auditedDate
                        ? toBackendDateFormat(auditedDate instanceof Date ? auditedDate : auditedDate.toDate())
                        : undefined
                }
            })
        }
    }

    function handleDeleteDocument(_document: any) {
        setDocument(_document);
        setAction(ACTIONS.DELETE);
    }

    function handleSubmit() {
        setAction(ACTIONS.NONE);
        submitComplianceActivity({
            compliance: {
                id: data.id,
                status: "Submitted",
                audited_date: auditedDate
                    ? toBackendDateFormat(auditedDate instanceof Date ? auditedDate : auditedDate.toDate())
                    : undefined,
                forms_status_remarks: submitComment.trim() || undefined
            }
        });
    }

    function handleUpdateAuditDate() {
        updateAuditDate({
            compliance: {
                id: activityDetails.id,
                audited_date: auditedDate
                    ? toBackendDateFormat(auditedDate instanceof Date ? auditedDate : auditedDate.toDate())
                    : undefined
            }
        });
    }

    function handleSaveComment() {
        updateAuditDate({
            compliance: {
                id: activityDetails.id,
                forms_status_remarks: submitComment.trim() || undefined
            }
        });
    }

    function handleRecall() {
        setRecallRemarks('');
        setRecallDueDate(activityDetails.due_date ? dayjs(activityDetails.due_date).format('YYYY-MM-DD') : '');
        setAction(ACTIONS.RECALL);
    }

    function handleConfirmRecall() {
        if (!recallRemarks.trim()) {
            toast.warning('Recall remarks are required.');
            return;
        }
        if (!recallDueDate) {
            toast.warning('Due date is required.');
            return;
        }
        recallCompliance({
            id: activityDetails.id,
            recall_remarks: recallRemarks.trim(),
            due_date: toBackendDateFormat(new Date(recallDueDate))
        });
    }

    function handleFileUpload(files: FileList) {
        const formData = new FormData();
        Array.from(files).forEach(_file => {
            formData.append('file', _file, _file.name);
            formData.append('compliance_id', data.id);
        });
        uploadDocument({
            formData
        });
    }

    useEffect(() => {
        if (Boolean(activity)) {
            const { act_state_mapping } = activity;
            const { rule_compliance_detail } = act_state_mapping;
            setActivity(activity);
            setRuleCompliance(rule_compliance_detail);
            const _editable = isFormEditable(activity.status) && Boolean(rule_compliance_detail);
            setEditable(_editable);
            setInitialValue({
                hideButtons: !(_editable && hasUserAccess(USER_PRIVILEGES.OWNER_ACTIVITIES_DOCUMENT_UPLOAD)),
                buttonWrapStyles: styles.btnGroup,
                submitBtnText: 'Upload'
            });
            if (hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)) {
                const existingComment = (getValue(activity, 'forms_status_remarks') || '').trim();
                setSubmitComment(existingComment);
            }
            if (hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD)) {
                const existingDate = activity.audited_date;
                const d = existingDate && dayjs(existingDate);
                const hasValidDate = d && d.isValid() && d.year() > 100;
                const initial = hasValidDate
                    ? new Date(existingDate)
                    : activity.status === ComplianceActivityStatus.PENDING ? new Date() : undefined;
                setAuditedDate(initial || null);
                setAuditDateInitial(initial);
            }
        }
    }, [activity])

    return (
        <>
            <div className={styles.overlay} style={{left: '74px', top: '-18px', width: 'calc(100vw - 74px)'}}>
                {
                    Boolean(activity) &&
                    <div className="card border-0 h-full w-full">
                        <div className="d-flex flex-row justify-space-between px-4 pt-4 align-items-center">
                            <TitleTmpl name={getValue(data, 'activity.name')} status={data.status} />
                            <Icon name="close" action={onCancel} className="ms-auto" style={{cursor: "pointer"}} />
                        </div>
                        <div className={`d-flex flex-column justify-space-between p-4 ${styles.activitydetailsContainer} `}>
                            <div className="horizontal-form">
                                <FormRenderer FormTemplate={FormTemplate}
                                    initialValues={{ ...activity, hideButtons: true }}
                                    componentMapper={ComponentMapper}
                                    schema={schema}
                                />

                            </div>
                            {
                                getValue(activityDetails, 'is_notice') &&
                                <div className="horizontal-form">
                                    <FormRenderer FormTemplate={FormTemplate}
                                        initialValues={{ hideButtons: true }}
                                        componentMapper={ComponentMapper}
                                        schema={schema3}
                                    />
                                </div>
                            }
                            {
                                hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) &&
                                <FormRenderer FormTemplate={FormTemplate}
                                    initialValues={{hideButtons: true}}
                                    componentMapper={ComponentMapper}
                                    schema={schema2}
                                />
                            }
                            {
                                hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) && hasEditableAccess() &&
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Comments for Compliance Manager<span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        style={{width: "500px", resize: "vertical"}}
                                        placeholder="Enter your comments here..."
                                        value={submitComment}
                                        disabled={!editableForm}
                                        onChange={(e) => { setSubmitComment(e.target.value); }}
                                    />
                                </div>
                            }
                            {
                                (hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)) &&
                                <FormRenderer FormTemplate={FormTemplate}
                                    initialValues={{ hideButtons: true }}
                                    componentMapper={ComponentMapper}
                                    schema={schema2}
                                    debug={debugForm}
                                />
                            }
                            <div className="d-flex justify-content-end my-4">
                                {
                                    editableForm && hasEditableAccess() ?
                                        <>
                                            <Button variant="outline-secondary" className="btn btn-outline-secondary px-4" onClick={onCancel}>{'Cancel'}</Button>
                                            <Button variant="primary" onClick={performSubmit} disabled={hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) ? !submitComment.trim() : !form.valid} className="px-4 ms-3" >{'Submit'}</Button>
                                        </> :
                                        <>
                                            <Button variant="primary" onClick={onCancel} className="px-4 ms-3" >{'Close'}</Button>
                                            {
                                                (hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) || isAdmin()) &&
                                                data.status === ComplianceActivityStatus.APPROVED &&
                                                <Button variant="outline-danger" onClick={handleRecall} className="px-4 ms-3">{'Recall'}</Button>
                                            }
                                            {
                                                hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) &&
                                                (data.status === ComplianceActivityStatus.PENDING || data.status === ComplianceActivityStatus.APPROVED) &&
                                                (isAuditDateChanged
                                                    ? <Button variant="primary" onClick={handleUpdateAuditDate} className="px-4 ms-3">{'Save Date'}</Button>
                                                    : <Button variant="primary" onClick={handleSaveComment} className="px-4 ms-3" disabled={!form.valid}>{'Save'}</Button>)
                                            }
                                        </>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
            {
                action === ACTIONS.CONFIRM &&
                <ConfirmModal title={'Submit Compliance Activity'} onSubmit={handleSubmit} onClose={() => setAction(ACTIONS.NONE)}>
                    {
                        (documents || []).length === 0 &&
                        <div className="text-center text-warn mb-4">There are no supporting documents uploaded to this activity!</div>
                    }
                    <div className="text-center mb-4">Are you sure you want to submit this compliance activity? Please review all the attached evidences/documents before submitting.</div>
                </ConfirmModal>
            }
            {
                action === ACTIONS.RECALL &&
                <ConfirmModal
                    title={'Recall Compliance Activity'}
                    onSubmit={handleConfirmRecall}
                    onClose={() => setAction(ACTIONS.NONE)}
                    disableSubmit={!recallRemarks.trim() || !recallDueDate}
                >
                    <div className="text-center mb-3">Are you sure you want to recall this compliance activity? The status will be updated to Recalled and the owner will need to resubmit it.</div>
                    <div className="mb-2">
                        <label className="form-label fw-bold">Due Date<span className="text-danger">*</span></label>
                        <input
                            type="date"
                            className="form-control"
                            style={{ maxWidth: 220 }}
                            min={activityDetails && activityDetails.due_date ? dayjs(activityDetails.due_date).format('YYYY-MM-DD') : undefined}
                            value={recallDueDate}
                            onChange={(e) => setRecallDueDate(e.target.value)}
                        />
                    </div>
                    <div className="mb-2">
                        <label className="form-label fw-bold">Recall Remarks<span className="text-danger">*</span></label>
                        <textarea
                            className="form-control"
                            rows={3}
                            style={{ resize: "vertical" }}
                            placeholder="Enter remarks for recalling this activity..."
                            value={recallRemarks}
                            onChange={(e) => setRecallRemarks(e.target.value)}
                        />
                    </div>
                </ConfirmModal>
            }
            {
                recalling && <PageLoader />
            }
            {
                action === ACTIONS.DELETE && Boolean(document) &&
                <ConfirmModal title={'Alert!'} onSubmit={() => deleteDocument(document.id)} onClose={() => setAction(ACTIONS.NONE)}>
                    <div className="text-center mb-4">
                        {document.evidence_type === EVIDENCE_TYPE.AUTOMATED
                            ? 'Are you sure you want to remove this auto generated evidence? You will have to attach it manually later.'
                            : 'Are you sure you want to remove this attached evidence? If required, you will have to attach it manually later.'}
                    </div>
                </ConfirmModal>
            }
        </>
    )
}
